import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useMobileEdgeChromeStore } from "../../stores/mobileEdgeChromeStore";
import { logger } from "../../utils/logger";

const Select = React.forwardRef(
  (
    {
      options = [],
      value,
      onChange,
      disabled = false,
      className = "",
      placeholder = "Select an option...",
      loading = false,
      size = "default",
      style,
      chevronClassName = "",
      onOpenChange,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      left: 0,
      width: 0,
    });
    const selectRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
          // Check if the click is on a dropdown option (which is in a portal)
          const isDropdownClick = event.target.closest("[data-select-dropdown]");
          if (!isDropdownClick) {
            setIsOpen(false);
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    useEffect(() => {
      if (!isMobile || !isOpen) return;
      const { notifyMenuOverlayOpenChange } = useMobileEdgeChromeStore.getState().actions;
      notifyMenuOverlayOpenChange(true);
      return () => notifyMenuOverlayOpenChange(false);
    }, [isMobile, isOpen]);

    // Calculate dropdown position when opening
    useEffect(() => {
      if (isOpen && selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    }, [isOpen]);

    const handleSelect = (optionValue) => {
      // console.log("Select option clicked:", optionValue);
      // console.log("Current onChange function:", onChange);
      if (onChange) {
        onChange(optionValue);
      } else {
        logger.warn("onChange function is not provided");
      }
      setIsOpen(false);
    };

    const selectedOption = options.find((option) => option.value === value);
    const isCompact = String(size || "").toLowerCase() === "compact";
    const buttonStyle = isCompact ? { padding: "4px 8px", fontSize: "12px" } : null;
    const mergedButtonStyle =
      buttonStyle && style && typeof style === "object"
        ? { ...buttonStyle, ...style }
        : style || buttonStyle || undefined;
    const compactOptionStyle = isCompact
      ? { padding: "4px 8px", fontSize: "12px", lineHeight: "1.25" }
      : undefined;

    // Debug logging
    // console.log("Select component state:", {
    //   isOpen,
    //   options: options.length,
    //   value,
    //   selectedOption,
    //   hasOnChange: !!onChange,
    // });

    return (
      <div ref={selectRef} className={`relative ${className}`}>
        <button
          ref={ref}
          type="button"
          className={`menu-select-button ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          style={mergedButtonStyle}
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          {...props}
        >
          <span className="truncate">
            {loading ? "Loading..." : selectedOption ? selectedOption.label : placeholder}
          </span>
          {loading ? (
            <div
              className={`border-2 border-gray-400 border-t-transparent rounded-full animate-spin ${
                isCompact ? "w-3.5 h-3.5" : "w-4 h-4"
              }`}
            />
          ) : (
            <ChevronDown
              className={`${isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} text-gray-400 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              } ${chevronClassName}`}
            />
          )}
        </button>

        {isOpen &&
          createPortal(
            <div
              className={`fixed menu-dropdown-content ${isCompact ? "text-xs" : ""}`}
              data-select-dropdown
              style={{
                zIndex: 9999,
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
            >
              <div className={`max-h-[70vh] overflow-auto ${isCompact ? "py-0.5" : "py-1"}`}>
                {options.length === 0 ? (
                  <div
                    className={`${isCompact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"} text-gray-400`}
                  >
                    No options available
                  </div>
                ) : (
                  options.map((option) => {
                    const isSelected = value === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`menu-select-option ${isCompact ? "px-2 py-1 text-xs" : ""} ${
                          isSelected ? "selected" : ""
                        } ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        style={compactOptionStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!option.disabled) {
                            handleSelect(option.value);
                          }
                        }}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }
);

Select.displayName = "Select";

export { Select };
